export { default as VERSION_INFO } from "../version";

export const SYSTEM_IDENTITY = {
  DEFAULT_INTEGRATION_NAME: "singleIntegration",
  DEFAULT_ADMIN_INTEGRATION_NAME: "vb-backend-integration",
  ADMIN_SETTINGS_KEY: "admin_config_toggles",

  MODAL_CONTAINER_ID: "ws-modal-container",
  SCRIPT_CONTAINER_ID: "ws-integration-init-scripts",
  GET_SCRIPT_CONTAINER_ID: (scriptId: string) => `ws-integration-${scriptId}`,
  TOAST_CONTAINER_ID: "ws-toast-container",
  CART_FORM_NAME: "cart-form",
  CHECKOUT_FORM_NAME: "checkout-form",

  ADD_TO_CART_ID: "ws-add-to-cart-popup",
} as const;

export const ECourseColumns = {
  TIMESPAN: "timespan",
  OFFER: "offer",
  COURSES: "courses",
  COURSE: "course",
  PLACES: "places",
  PLACE: "place",
  FROM: "from",
  TRAINER: "trainer",
  TO: "to",
  FROM_DATE_TO_DATE: "from_date_to_date",
  FROM_DATE: "from_date",
  MONTH: "month",
  TO_DATE: "to_date",
  FROM_TIME: "from_time",
  TO_TIME: "to_time",
  DURATION: "duration",
  CATEGORIES: "categories",
  PRICE: "price",
  INFO: "info",
  APPOINTMENTS: "appointments",
  APPOINTMENT_LIST: "appointmentlist",
  REMAINING: "remaining",
  DESCRIPTION: "infolink",
  BOOKLINK: "booklink",
} as const;

// Export the bootstrap classes individually so the classic loader does not
// include the full application class registry.
export const WIDGET_ERROR_CLASS = "ws-widget-error";
const WIDGET_INTERACTION_CLASS = "ws-widget-interaction";

export const CLASS_CONSTANTS = {
  WIDGET_ERROR: {
    ROOT: WIDGET_ERROR_CLASS,
    INTERACTION: WIDGET_INTERACTION_CLASS,
  },
  POWERED_BY: {
    ROOT: "ws-powered-by",
    LINK: "ws-powered-by-link",
  },
  MAIN: {
    WEBSAIL: "websail",
    CONTAINER: "ws-container",
    THEME: (theme?: string) => (theme ? `ws-${theme}-theme` : ""),
    INTEGRATION_NAME: (name?: string) => (name ? `ws-${name}` : ""),
  },
  UTILITIES: {
    HIDDEN: "ws-hidden",
    CURSOR_POINTER: "ws-cursor-pointer",
    FLEX: "ws-flex",
    FLEX_NOWRAP: "ws-flex-nowrap",
    FLEX_ROW: "ws-flex-row",
    FLEX_COLUMN: "ws-flex-column",

    TEXT_ALIGN_LEFT: "ws-text-align-left",
    TEXT_ALIGN_RIGHT: "ws-text-align-right",
    TEXT_ALIGN_CENTER: "ws-text-align-center",

    TEXT_DANGER: "ws-text-danger",
    TEXT_WARNING: "ws-text-warning",
    TEXT_SUCCESS: "ws-text-success",
    TEXT_INFO: "ws-text-info",

    TEXT_BOLD: "ws-text-bold",
  },
  SEMANTIC: {
    TWO_COLUMN: "ws-two-sided-column",
    LEFT_COLUMN: "ws-left-column",
    RIGHT_COLUMN: "ws-right-column",
    BLOCK: "ws-block",
    SVG: "ws-svg",
    TYPED_SVG: (type: string) => `ws-svg-${type}`,
    INLINE_FIELDS: "ws-inline-fields",
    CALENDAR: "ws-calender",
    SPINNER: "ws-spinner",
    NO_DATA_TO_DISPLAY: "ws-nothing-to-display",
    BILLER_META: "ws-biller-meta",
    STEP_META: "ws-step-meta",
    PAGINATION: "ws-pagination",

    FORM_DESCRIPTION: "ws-form-description",

    USER_BUTTONS: "ws-user-buttons",

    FIELD_SET: {
      ROOT: "ws-fieldset",
      TITLE: "ws-fieldset-title",
      LEGEND: "ws-fieldset-legend",
      DESCRIPTION: "ws-fieldset-description",
      CONTENT: "ws-fieldset-content",
    },

    PARTICIPANT_GROUP: "ws-participant-group",
    PARTICIPANT_GROUP_FLAT: "ws-participant-group-flat",
    REGISTER_DIFFERENT_PARTICIPANTS: "ws-register-different-participants",

    MODAL: {
      ROOT: "ws-modal",
      OVERLAY: "ws-modal-overlay",
      CONTENT: "ws-modal-content",
      CONTENT_INNER: "ws-modal-content-inner",
      CLOSE: "ws-modal-close",

      HEADER: "ws-modal-header",
      BODY: "ws-modal-body",
      FOOTER: "ws-modal-footer",
    },

    TABS: {
      TAB: "ws-tab",
      ROOT: "ws-tabs",
      BAR: "ws-tab-bar",
      CONTENT: "ws-tab-content",
      ACTIVE: "ws-tab-active",
    },

    TABLE: {
      ROOT: "ws-table",
      COL_GROUP: "ws-table-colgroup",
      COL_GROUP_COL: "ws-table-col",
      HEAD: {
        ROOT: "ws-table-head",
        ROW: "ws-head-row",
        CELL: "ws-header-cell",
      },
      FOOTER: {
        ROOT: "ws-table-foot",
      },
      BODY: {
        ROOT: "ws-table-body",
        ROW: "ws-data-row",
        ROW_BOOKED_OUT: "ws-data-row-booked-out",
        ROW_FEW_PLACES_LEFT: "ws-data-row-few-places-left",
        ROW_HAS_APPOINTMENTS: "ws-data-row-has-appointments",
        ROW_HAS_PRICE: "ws-data-row-has-price",
        ROW_HAS_TRAINERS: "ws-data-row-has-trainers",
        ROW_IS_PAST_DUE: "ws-data-row-is-due",
        ROW_WAITLIST: "ws-data-row-waitlist",
        ROW_EXPANDED: "ws-data-row-expanded",
        CELL: "ws-cell",
        CELL_SPAN: "ws-cell-span",
        CELL_LINK: "ws-cell-link",
        CELL_BOOK_LINK_WAITING_LINK: "ws-waiting-list-link",
        CELL_BOOK_NOW_LINK: "ws-book-now-link",
        CELL_BOOKED_OUT: "ws-cell-booked-out",
        DESCRIPTION_ROW: "ws-data-description-row",
      },

      CELL: (cellName?: string) => `ws-${cellName ? cellName + "-" : ""}cell`,
    },
  },
  INPUT: {
    DATE_TILE: "ws-date-tile",
    DATE_PICKER_PORTAL: "ws-date-picker-portal",
    TITLE: "ws-input-label",

    WITH_BUTTON_GROUP: "ws-input-with-button-group",

    SWITCH: "ws-yes-no",
    SWITCHABLE_FIELD: "ws-switchable-field",
    RADIO_GROUP: "ws-radio-group",
    RADIO_GROUP_LABEL: "ws-radio-group-label",
    INLINE_FLEX: "ws-inline-flex",
    GROUP: "ws-input-group",

    REQUIRED: "ws-required-hint",

    TYPED_INPUT_CONTAINER: (type?: string) => type && `ws-${type}-container`,
    NAMED_INPUT_CONTAINER: (name?: string) => name && `${name}-container`,
    NAMED_INPUT: (name?: string) => name && `ws-${name}-input`,
    NAMED_TITLE: (name?: string) => name && `ws-${name}-label`,
    TYPE: (type: string) => `ws-${type}`,
    TEXT_INPUT: "ws-text-input",
    FILE: {
      INPUT: "ws-file-input",
      SELECTED_PREVIEW: "ws-selected-files-preview",
      IMAGE: "ws-image-file",
      PREVIEW_NAME: "ws-file-preview-name",
      DROP_HINT: "ws-file-drop-hint",
    },
    ROOT: "ws-input",
    WRAPPER: "ws-input-wrapper",
    DESCRIPTION: "ws-input-description",
    RECOMMENDED: "ws-input-recommended",
    RECOMMENDED_TITLE: "ws-recommended-title",
    RECOMMENDED_INNER: "ws-recommended-wrap",
    SELECT: "ws-select",
    SELECT_PLACEHOLDER_SHOWN: "ws-select-placeholder-shown",
    SEARCH_SELECT: {
      ROOT: "ws-search-select-options",
      OPTION: "ws-search-select-option",
      OPTION_LIST: "ws-search-select-options-list",
      OPTION_DISABLED: "ws-search-select-option-disabled",
      INPUT_CONTAINER: "ws-search-n-select-input-container",
      SEARCH_INPUT: "ws-search-select-input",
      SEARCH_QUERY_CONTAINER: "ws-search-select-query",
      SEARCH_QUERY_INPUT: "ws-search-select-query-input",
      GROUP_HEADER: "ws-search-select-group-header",
    },
    HIDDEN: "ws-hidden",
    IS_INVALID: "ws-is-invalid",
    IS_VALID: "ws-is-valid",
  },
  BUTTON: {
    ROOT: "ws-button",
    ICON: "ws-button-icon",
    ICON_LEFT: "ws-button-icon-left",
    ICON_RIGHT: "ws-button-icon-right",
    TEXT: "ws-button-text",
    FORM_SUBMIT: "ws-button-submit",
    FORM_BACK: "ws-button-back",
    FORM_BUTTONS: "ws-form-buttons",
    FLUID: "ws-button-fluid",
    FORM_BUTTONS_RIGHT: "ws-form-buttons-right",
    VARIANT: (variant: string) => `ws-button-${variant}`,
  },
  MENU: {
    ROOT: "ws-menu",
    DROPDOWN: "ws-menu-dropdown",
    ITEM: "ws-menu-item",
    CHECKBOX: "ws-menu-checkbox",
  },
  COURSES: {
    ROOT: "ws-course-list",
    TABLE: "ws-table-courses",
    TABLE_CONTAINER: "ws-table-courses-container",
    ACCORDION: {
      ROOT: "ws-course-accordion",
      HEADER_TITLE_WRAPPER: "ws-course-accordion-title-wrapper",
    },
    GRID: "ws-course-grid",
    OFFERS: {
      ROOT: "ws-offers",
      FOOTER: "ws-offers-footer",
      ITEM: {
        ROOT: "ws-offer-item",
        SELECTED: "ws-offer-selected-item",
        INFO: "ws-offers-item-info",
        CELL_CAPTION: "ws-offer-cell-caption",
        CELL_ICON: "ws-offer-cell-icon",
        CELL_VALUE: "ws-offer-cell-value",
        CELL_VALUE_DESCRIPTION: "ws-offer-cell-value",
        CELL: "ws-offer-cell",
      },
      GROUP: {
        HEADER: "ws-offer-group-header",
        NAMED_HEADER: (name?: string) =>
          name && `ws-offer-group-${name}-header`,
        RECORDS: () => "ws-offer-group-records",
        NAMED_RECORDS: (name?: string) =>
          name && `ws-offer-group-${name}-records`,
      },
      CHECKBOX: {
        ROOT: "ws-offer-checkbox",
        ICON_WRAPPER: "ws-offer-checkbox-icon-wrapper",
      },

      CALENDAR: {
        EVENT_WRAP: "ws-offer-calendar-event-wrap",
      },
    },
    GRID_ITEM: "ws-course-grid-item",
    GRID_ITEM_CAPTION: "ws-course-grid-item-caption",
    GRID_ITEM_BODY: "ws-course-grid-item-body",
    INFO: "ws-course-info",
    FILTERS: "ws-course-filters",
    META: {
      ROOT: "ws-course-meta",
      NAMED_META: (name?: string) => name && `ws-${name}-course-meta`,
      TITLE: "ws-course-meta-title",
      TIMESPAN_TITLE: "ws-timespan-title",
      TIMESPAN_DESCRIPTION: "ws-timespan-description",
      COURSE_TITLE: "ws-course-title",
      COURSE_DESCRIPTION: "ws-course-description",
      TIMESPAN_TITLE_PRICE: "ws-timespan-title-price",
    },
    APPOINTMENTS: {
      LIST: "ws-appointment-list",
      SHOW_LESS: "ws-appointment-list-show-less",
      SHOW_MORE: "ws-appointment-list-show-more",
      SHOW_MORE_TOGGLE: "ws-appointment-list-toggle",
    },
    COLUMNS: {
      [ECourseColumns.COURSE]: "ws-course-cell",
      [ECourseColumns.PLACE]: "ws-place-cell",
      [ECourseColumns.FROM]: "ws-from-cell",
      [ECourseColumns.TO]: "ws-to-cell",
      [ECourseColumns.PRICE]: "ws-price-cell",
      [ECourseColumns.TRAINER]: "ws-trainer-cell",
      [ECourseColumns.INFO]: "ws-info-cell",
      [ECourseColumns.FROM_DATE]: "ws-from-date-cell",
      [ECourseColumns.TO_DATE]: "ws-to-date-cell",
      [ECourseColumns.FROM_TIME]: "ws-from-time-cell",
      [ECourseColumns.TO_TIME]: "ws-to-time-cell",
      [ECourseColumns.CATEGORIES]: "ws-categories-cell",
      [ECourseColumns.DURATION]: "ws-duration-cell",
      [ECourseColumns.FROM_DATE_TO_DATE]: "ws-from-date-to-date-cell",
      [ECourseColumns.APPOINTMENTS]: "ws-appointments-cell",
      [ECourseColumns.REMAINING]: "ws-remaining-cell",
      [ECourseColumns.DESCRIPTION]: "ws-description-cell",
      [ECourseColumns.BOOKLINK]: "ws-booklink-cell",
    },

    COURSE_NAME: (courseName: string) => `ws-course-${courseName}`,
    TAG_NAME: (tagName: string) => `ws-tags-${tagName}`,
  },
  COURSE_CATEGORIES: {
    ROOT: "ws-course-categories",
    GRID: "ws-course-categories-grid",
    LIST: "ws-course-categories-list",
    RECORD: "ws-course-category",
    RECORD_THUMB: "ws-course-category-thumb",
    RECORD_THUMB_EMPTY: "ws-course-category-thumb--empty",
    RECORD_TITLE: "ws-course-category-title",
    RECORD_DESCRIPTION: "ws-course-category-description",
    RECORD_BOOKLINK: "ws-course-category-booklink",
    RECORD_NEXT_APPOINTMENT: "ws-course-category-next-appointment",
  },
  NOTIFICATION: {
    ROOT: "ws-notification",
    CONTAINER: "ws-notification-wrap",
    FLASH: "ws-flash-notification ws-flashnotifications",
    HEADER: "ws-notification-header",
    CLOSE: "ws-notification-close",
    TYPE: {
      danger: "ws-notification-danger",
      success: "ws-notification-success",
      info: "ws-notification-info",
      warning: "ws-notification-warning",
    },

    VALIDATION_MESSAGE: {
      ROOT: "ws-validation-message",
      LIST: "ws-validation-message-list",
      HEADER: "ws-validation-message-header",
    },
  },
  PRICE: {
    DESCRIPTION: "ws-price-description",

    SUMMARY: {
      ROW: "ws-price-row",
      LINE_TOTAL: "ws-price-line-total",
      TOTAL: "ws-price-total",
      SUB_PRICE: "ws-sub-price",
      EXTRA_PRICE: "ws-extra-price",
      TITLE: "ws-price-title-cell",
      COUNT_OF_PARTICIPANTS: "ws-count-of-participants",
      BREAKUP: "ws-price-breakup",
      TOGGLE_BUTTON: "ws-price-breakup-toggle-button",
      HEADER: "ws-price-header",
      WAITLIST: "ws-price-summary-waitlist",
      HAS_NO_PRICE: "ws-no-price",
    },

    PAYMENT_PAGE: {
      TITLE: "ws-price-title-cell",
      ROOT: "ws-payment-page",
      BILLS: "ws-bills",
      COLLAPSE: "ws-price-collapse",
      EXPAND: "ws-price-expand",
      CHECKBOX_TITLE: "ws-price-title",
      QUANTITY: "ws-price-quantity-cell",
      AMOUNT: "ws-price-amount-cell",
      ROW: "ws-price-row",

      COUPON_AND_BUTTONS: "ws-coupon-code-and-buttons",

      COUPON_CODE: {
        ROOT: "ws-coupon-code",
        BUTTON: "ws-coupon-code-button",
        BUTTON_APPLY: "ws-coupon-code-button-apply",
        BUTTON_CLEAR: "ws-coupon-code-button-clear",

        CONTAINER: "ws-coupon-code-container",
        SUCCESS: "ws-coupon-code--success",
        ERROR: "ws-coupon-code--error",

        FIELD_WRAP: "ws-coupon-code-input-wrapper",
      },

      PAYMENT_GATEWAY: {
        CONTAINER: "ws-payment-gateway-container",
        ROOT: "ws-payment-gateway",
        RECOMMENDED: "ws-payment-gateway-recommended",
        RECOMMENDED_LABEL: "ws-payment-gateway-recommended-label",
        IMAGE: "ws-payment-gateway-image",
        INFO: "ws-payment-gateway-info",
        RADIO: "ws-payment-gateway-radio",
        RADIO_SELECTED: "ws-payment-gateway-radio-selected",
        RADIO_INVALID: "ws-payment-gateway-radio-invalid",
      },
    },

    SEPA_DIRECT_DEBIT: {
      ROOT: "ws-sepa-direct-debit",
      TITLE: "ws-sepa-direct-debit-title",
      DESCRIPTION: "ws-sepa-direct-debit-description",
    },
  },
  BANK_TRANSFER: {
    BANK: "ws-bank-transfer-bank",
    LABEL: "ws-bank-transfer-label",
    VALUE: "ws-bank-transfer-value",
    PURPOSE: "ws-payment-purpose",
    DETAILS: "ws-bank-transfer-details",
    MESSAGES: "ws-bank-transfer-messages",
    MESSAGE: "ws-bank-transfer-messages",
    INFO: "ws-bank-transfer-info",
  },

  TIMESPAN: {
    LAST_COURSE: "ws-course-last",
    TITLE_PRICE: "ws-timespan-title-price",

    CART_EXTRA_ITEM: "ws-cart-extra-item",
    CART_ITEM: "ws-cart-item",
  },
  CART: {
    ROOT: "ws-cart",
    TABLE: {
      ROOT: "ws-cart-table",
      WRAPPER: "ws-cart-wrapper",
      ITEM: "ws-cart-item",
      ROW: "ws-cart-row",
      ROW_UI: "ws-cart-row-ui",
      ROW_DESCRIPTION: "ws-cart-row-description",
      TOTAL: "ws-cart-total",
    },
    CELL: (cell: string) => `ws-cart-${cell}`,
    ADD_TO_CART_BUTTON: "ws-add-to-cart-button",
    PREVIEW: {
      ICON_WRAP: "ws-cart-icon-wrap",
      ROOT: "ws-cart-preview",
      FLOATING: "ws-cart-preview-floating",
      INLINE: "ws-cart-preview-inline",
      ITEM_COUNT: "ws-cart-count",
      TEXT: "ws-cart-preview-text",
      ICON: "ws-cart-preview-icon",
      QUANTITY: "ws-cart-preview-quantity",
      TOTAL: "ws-cart-preview-total",
    },
    EMPTY: "ws-cart-empty",
  },
  ADD_TO_CART: {
    FORM: "ws-add-to-cart-form",
    BUTTON: "ws-add-to-cart-button",
    POPUP: "ws-add-to-cart-popup",
    BUTTON_TO_CART: "ws-to-cart-button",
    BUTTON_BOOK_NOW: "ws-to-book-now-button",
    BUTTON_CONTINUE: "ws-to-continue-button",
  },
  TIME_SLOTS: {
    ALL_SLOTS: "ws-timeslots",
    SLOT: "ws-timeslot",
    SLOT_SELECTED: "ws-timeslot-selected",
    SLOT_DISABLED: "ws-timeslot-disabled",
    FILTER: "ws-timeslot-filter",
    FILTER_SELECTED: "ws-timeslot-filter-selected",
    TIME_SLOT_HIDDEN: "ws-time-slots-hidden",
  },

  ACCORDION: {
    ROOT: "ws-accordion",
    PANE: "ws-accordion-pane",
    HEADER: "ws-accordion-header",
    ARROW: "ws-accordion-arrow",
    TITLE: "ws-accordion-title",
    ICONS: "ws-accordion-icons",
    BODY: "ws-accordion-body",
    OPEN: "ws-accordion-open",
    CLOSED: "ws-accordion-close",
    STATE: "ws-state",
    VALID: "ws-accordion-valid",
    INVALID: "ws-accordion-invalid",
  },

  WIZARD: {
    ROOT: "ws-wizard",
    STEP: "ws-step",
    STEPS: "ws-wizard-steps",
    STEP_WRAPPER: "ws-wizard-step-wrapper",
    STEP_CONTENT: "ws-wizard-step-content",
    STEP_ICON: "ws-wizard-step-icon",
    STEP_TEXT: "ws-wizard-step-text",
    STEP_INDICATOR: "ws-wizard-step-indicator",
    STEP_NUMBER: "ws-wizard-step-number",
    NAV: "ws-step-nav",
    NAV_DISABLED: "ws-step-nav-disabled",
    NAV_SELECTED: "ws-step-nav-selected",
    NAV_TITLE: "ws-step-nav-title",
    NAV_GROUP_LABEL: "ws-step-nav-group-title",
    STEP_DESCRIPTION: "ws-wizard-description",
    NAV_GROUP: "ws-step-nav-group",
    NAV_GROUPPED: "ws-step-nav-groupped",
    CONTENT: "ws-wizard-content",
    NAV_BUTTON: "ws-wizard-nav-button",
    NAV_BUTTON_PREVIOUS: "ws-wizard-nav-button-previous",
    NAV_BUTTON_NEXT: "ws-wizard-nav-button-next",
  },

  TOAST: {
    ROOT: "ws-toast",
    CONTAINER: "ws-toast-container",
    PROGRESS: "ws-toast-progress",
    CONTEXT: {
      success: "ws-toast-success",
      danger: "ws-toast-danger",
      error: "ws-toast-danger",
      info: "ws-toast-info",
      warning: "ws-toast-warning",
      default: "",
    },
  },

  AVATAR: {
    CHIP: "ws-chip",
    TYPED_CHIP: (type: string) => type && `ws-${type}-chip`,
    CHIP_LABEL: "ws-chip-label",
    ROOT: "ws-avatar",
    LABEL: "ws-avatar-label",
    IMAGE: "ws-avatar-image",
  },

  MESSAGES: "ws-messages",
  MESSAGE: {
    ROOT: "ws-message",
    CATEGORY: (categoryName: string) => `ws-message-category-${categoryName}`,
    HEADER: "ws-message-attachment-header",
    TITLE: "ws-message-attachment-title",
    ATTACHMENTS: "ws-message-attachments",
    ATTACHMENT: "ws-message-attachment",
    ATTACHMENT_ICON: "ws-message-attachment-icon",
    BODY: "ws-message-body",
    DRAFT_MESSAGE: "ws-message-drafted",
  },
  SIGN_IN: {
    ROOT: "ws-button-signin",
    FORM: "ws-signin-form",
    BUTTON: "ws-button-signin-button",
    BUTTON_ERRORED: "ws-button-signin-button-errored",
  },

  AUTH: {
    ENTITY_TABLE: "ws-entity-table",
    ENTITY_HEAD_CELL_PREFIX: "ws-entity-header-cell",
    USER: {
      SUMMARY: "ws-user-summary",
      SIGN_IN: "ws-button-user-sign-in",
      AUTHENTICATED: "ws-button-user-authenticated",
      AUTHENTICATED_MENU: "ws-user-menu",
      NEEDS_TO_PAY: "ws-user-due-amount",
      NEXT_APPOINTMENT: "ws-next-appointment",
    },
  },

  CANCEL_BOOKING_FORM: {
    ROOT: "ws-cancel-booking-form",
    COURSE_META: "ws-event-to-cancel-course-meta",
  },

  CHECKOUT_FORM: {
    ROOT: "ws-checkout-form",
  },

  CONSENT_FORM: {
    ROOT: "ws-consent-form",
  },

  BOOKCART_FORM: {
    ROOT: "ws-booking-form",
  },
} as const;

export const FORM_CONSTANTS = {
  PAYMENT_FORM: "payment-form",
  SEPA_MANDATE_FORM: "sepa-mandate-form",
  UPDATE_CONSENT_FORM: "update-consent-form",
};

export type ECourseColumns =
  (typeof ECourseColumns)[keyof typeof ECourseColumns];

export const FilterColumns = {
  courses: "courses",
  months: "months",
  places: "places",
  search: "search",
  categories: "categories",
} as const;

export const filterableColumns = [
  "courses",
  "places",
  "months",
  "search",
] as const;

export type FilterableColumnType =
  (typeof FilterColumns)[keyof typeof FilterColumns];
export type CourseFiltersType = Record<FilterableColumnType, string[]>;
